import { createAsyncThunk } from "@reduxjs/toolkit";
import { companySearch } from "../../../api/post/companySearchApi";
import { 
  setCompanyList, 
  setSearchedCompany, 
  setCompanyPastOrders, 
  setCompanyActiveOrders,
  clearCompanyDataExceptList,
  setCreatedCompany,
  addAddressToCompany,
  setBankNamesList,
  setAccountManagerList,
  setBankNamesStatus,
  setAccountManagersStatus
} from "./companySlice";
import { companyAddressCreate } from "../../../api/post/companyAddressCreateApi";
import { getBankNamesList } from "../../../api/get/getBankNamesApi";
import { getAccountManagersList } from "../../../api/get/getAccountManagersApi";

// Thunk to fetch bank names if not already in state
export const fetchBankNamesAsync = () => async (dispatch, getState) => {
  const { company } = getState();
  // Exit if data is already loaded or is currently loading
  if (company.bankNamesStatus === 'loading' || company.bankNamesStatus === 'succeeded') {
    return;
  }
  
  try {
    dispatch(setBankNamesStatus('loading'));
    const response = await getBankNamesList();
    let bankNames = [];
    if (Array.isArray(response)) {
      bankNames = response;
    } else if (response && Array.isArray(response.data)) {
      bankNames = response.data;
    }
    dispatch(setBankNamesList(bankNames));
    dispatch(setBankNamesStatus('succeeded'));
  } catch (error) {
    console.error("Failed to fetch bank names:", error);
    dispatch(setBankNamesStatus('failed'));
    dispatch(setBankNamesList([])); // Dispatch empty array on error
  }
};

// Thunk to fetch account managers if not already in state
export const fetchAccountManagersAsync = () => async (dispatch, getState) => {
  const { company } = getState();
  // Exit if data is already loaded or is currently loading
  if (company.accountManagersStatus === 'loading' || company.accountManagersStatus === 'succeeded') {
    return;
  }
  
  try {
    dispatch(setAccountManagersStatus('loading'));
    const response = await getAccountManagersList();
    let managers = [];
    if (Array.isArray(response)) {
      managers = response;
    } else if (response && Array.isArray(response.data)) {
      managers = response.data;
    }
    dispatch(setAccountManagerList(managers));
    dispatch(setAccountManagersStatus('succeeded'));
  } catch (error) {
    console.error("Failed to fetch account managers:", error);
    dispatch(setAccountManagersStatus('failed'));
    dispatch(setAccountManagerList([])); // Dispatch empty array on error
  }
};


// Thunk to search for companies (gets all companies)
export const companySearchAsync = () => async (dispatch, getState) => {
  try {
    // Send null search to get all companies
    const response = await companySearch({ name: null });
    console.log("Company search API response:", response);
    
    // Ensure we always have an array - handle different API response formats
    let companiesArray = [];
    if (Array.isArray(response)) {
      companiesArray = response;
    } else if (response && Array.isArray(response.data)) {
      companiesArray = response.data;
    } else if (response && response.companies && Array.isArray(response.companies)) {
      companiesArray = response.companies;
    } else {
      console.warn("Unexpected API response format:", response);
      companiesArray = [];
    }
    
    dispatch(setCompanyList(companiesArray));
    return { success: true, data: companiesArray };
  } catch (error) {
    console.error("Company search failed:", error);
    // Ensure we set an empty array on error to prevent filter issues
    dispatch(setCompanyList([]));
    return { 
      success: false, 
      error: error.response?.data || { message: 'Failed to search companies' } 
    };
  }
};

// Thunk to search for specific company by name
export const searchSpecificCompanyAsync = (companyName) => async (dispatch) => {
  try {
    const response = await companySearch({ name: companyName });
    
    // Ensure we always have an array - handle different API response formats
    let companiesArray = [];
    if (Array.isArray(response)) {
      companiesArray = response;
    } else if (response && Array.isArray(response.data)) {
      companiesArray = response.data;
    } else if (response && response.companies && Array.isArray(response.companies)) {
      companiesArray = response.companies;
    }
    
    // If a specific company is found and selected, clear other company data
    if (companiesArray && companiesArray.length > 0) {
      const company = companiesArray.find(c => 
        c.name && c.name.toLowerCase().includes(companyName.toLowerCase())
      );
      
      if (company) {
        // Clear all company data except list when new company is selected
        dispatch(clearCompanyDataExceptList());
        dispatch(setSearchedCompany(company));
      }
    }
    
    return { success: true, data: companiesArray };
  } catch (error) {
    console.error("Specific company search failed:", error);
    return { 
      success: false, 
      error: error.response?.data || { message: 'Failed to search company' } 
    };
  }
};

// Thunk to select a company (clear data and set new company)
export const selectCompanyAsync = (company) => async (dispatch) => {
  try {
    // Clear all company data except list when new company is selected
    dispatch(clearCompanyDataExceptList());
    dispatch(setSearchedCompany(company));
    
    // TODO: If company has ID, fetch past orders and active orders
    // if (company?.id) {
    //   dispatch(companyPastOrdersAsync(company.id));
    // }
    
    return { success: true, data: company };
  } catch (error) {
    console.error("Company selection failed:", error);
    return { 
      success: false, 
      error: error.response?.data || { message: 'Failed to select company' } 
    };
  }
};

// Thunk for company past orders (to be implemented when API is available)
export const companyPastOrdersAsync = (companyId) => async (dispatch) => {
  try {
    // TODO: Implement when company past orders API is available
    // const pastOrders = await getCompanyPastOrdersApi(companyId);
    // dispatch(setCompanyPastOrders(pastOrders.data));
    // dispatch(setCompanyActiveOrders(pastOrders.data));
    
    console.log("Company past orders API not yet implemented for company:", companyId);
  } catch (error) {
    console.error("Company past orders search failed:", error);
  }
};

// Thunk for creating new company (to be implemented when needed)
export const createCompanyAsync = (companyData) => async (dispatch) => {
  try {
    // TODO: Implement when company creation API is available
    // const newCompany = await createCompany(companyData);
    // dispatch(setCreatedCompany(newCompany));
    // // Refresh company list to include new company
    // dispatch(companySearchAsync());
    
    console.log("Company creation API not yet implemented for:", companyData);
    return { success: true, data: companyData };
  } catch (error) {
    console.error("Company creation failed:", error);
    return { 
      success: false, 
      error: error.response?.data || { message: 'Failed to create company' } 
    };
  }
};

// Thunk for creating company address
export const createCompanyAddressAsync = (addressData, companyId) => async (dispatch) => {
  try {
    console.log("Creating company address:", addressData);
    
    // Call the company address create API
    const response = await companyAddressCreate(addressData);
    
    console.log("Company address created successfully:", response);
    
    // Extract just the data part from the API response
    const newAddress = response.data;
    
    // Add the new address to the company in Redux
    dispatch(addAddressToCompany({ 
      companyId: companyId, 
      newAddress: newAddress 
    }));
    
    return { success: true, data: newAddress };
  } catch (error) {
    console.error("Company address creation failed:", error);
    return { 
      success: false, 
      error: error.response?.data || { message: 'Failed to create company address' } 
    };
  }
}; 